
import * as ImagePicker from 'expo-image-picker';
import { Alert, Platform, Linking } from 'react-native';

// Android 13+ (API 33+) uses Photo Picker which doesn't require permissions
const ANDROID_PHOTO_PICKER_API_LEVEL = 33;

export class GalleryConsentService {
  /**
   * Check if we have gallery permission without requesting
   */
  static async hasPermission(): Promise<boolean> {
    try {
      if (Platform.OS === 'web') {
        return true; // Web handles permissions through browser
      }

      const { status } = await ImagePicker.getMediaLibraryPermissionsAsync();
      return status === 'granted';
    } catch (error) {
      console.error('❌ Error checking gallery permission:', error);
      return false;
    }
  }

  /**
   * Request gallery permission using system default prompts
   */
  static async requestPermission(): Promise<boolean> {
    try {
      console.log('🔍 Requesting gallery permission...');
      
      // For web, permissions are handled by the browser
      if (Platform.OS === 'web') {
        console.log('🌐 Web platform - permissions handled by browser');
        return true;
      }

      // Check current permission status first
      const currentPermission = await ImagePicker.getMediaLibraryPermissionsAsync();
      console.log('📱 Current permission status:', currentPermission.status);

      if (currentPermission.status === 'granted') {
        console.log('✅ Permission already granted');
        return true;
      }

      // If permission was denied before and can't ask again, show settings prompt
      if (currentPermission.status === 'denied' && currentPermission.canAskAgain === false) {
        console.log('❌ Permission permanently denied');
        return await this.showSettingsAlert();
      }

      // Request permission directly - system will show default prompt
      console.log('🔄 Requesting permission...');
      const permissionResult = await ImagePicker.requestMediaLibraryPermissionsAsync();
      console.log('📱 Permission result:', permissionResult.status);

      if (permissionResult.status === 'denied' && permissionResult.canAskAgain === false) {
        // User denied permanently, show settings alert
        return await this.showSettingsAlert();
      }

      return permissionResult.status === 'granted';
    } catch (error) {
      console.error('❌ Error requesting gallery permission:', error);
      return false;
    }
  }

  /**
   * Show alert directing user to settings
   */
  private static async showSettingsAlert(): Promise<boolean> {
    return new Promise((resolve) => {
      Alert.alert(
        'Permissão Necessária',
        'Para alterar sua foto de perfil, você precisa permitir o acesso à galeria nas configurações do seu dispositivo.',
        [
          {
            text: 'Cancelar',
            style: 'cancel',
            onPress: () => resolve(false),
          },
          {
            text: 'Abrir Configurações',
            onPress: async () => {
              try {
                await Linking.openSettings();
                resolve(false); // User needs to come back and try again
              } catch (error) {
                console.error('❌ Error opening settings:', error);
                resolve(false);
              }
            },
          },
        ]
      );
    });
  }

  /**
   * Launch image picker with optimized configuration for avatars
   */
  static async pickImage(): Promise<string | null> {
    try {
      console.log('📷 Launching image picker...');
      
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [1, 1], // Square aspect ratio for avatars
        quality: 0.8, // Good balance between quality and file size
        allowsMultipleSelection: false,
        exif: false, // Remove EXIF data for privacy
        base64: false, // Don't include base64 to save memory
      });

      if (result.canceled) {
        console.log('📷 Image picker cancelled');
        return null;
      }

      if (!result.assets?.[0]?.uri) {
        console.log('❌ No image URI in result');
        return null;
      }

      const selectedImage = result.assets[0];
      console.log('✅ Image selected:', {
        uri: selectedImage.uri,
        width: selectedImage.width,
        height: selectedImage.height,
        fileSize: selectedImage.fileSize,
      });

      return selectedImage.uri;
    } catch (error) {
      console.error('❌ Error picking image:', error);
      return null;
    }
  }

  /**
   * Check if the device uses Android Photo Picker (no permissions needed)
   * Android 13+ (API 33+) uses Photo Picker by default
   */
  static usesPhotoPicker(): boolean {
    if (Platform.OS !== 'android') {
      return false;
    }
    
    // Android 13+ uses Photo Picker which doesn't require permissions
    const apiLevel = Platform.Version;
    return typeof apiLevel === 'number' && apiLevel >= ANDROID_PHOTO_PICKER_API_LEVEL;
  }

  /**
   * Complete flow: check permission, request if needed, and pick image
   * On Android 13+, Photo Picker is used and no permissions are needed
   */
  static async requestPermissionAndPickImage(): Promise<string | null> {
    try {
      // Android 13+ uses Photo Picker - no permissions needed
      if (this.usesPhotoPicker()) {
        console.log('📱 Using Android Photo Picker (no permissions needed)');
        return await this.pickImage();
      }

      // iOS and older Android versions need permission
      const hasExistingPermission = await this.hasPermission();
      
      if (!hasExistingPermission) {
        // Request permission with proper UX
        const permissionGranted = await this.requestPermission();
        
        if (!permissionGranted) {
          console.log('❌ No permission granted');
          throw new Error('User rejected permissions');
        }
      }

      // Add small delay to ensure permission state is properly updated
      await new Promise(resolve => setTimeout(resolve, 200));

      // Pick image
      return await this.pickImage();
    } catch (error) {
      console.error('❌ Error in permission and pick flow:', error);
      
      // Re-throw permission errors so they can be handled specifically
      if (error instanceof Error && error.message.includes('permission')) {
        throw error;
      }
      
      return null;
    }
  }
}
